import { Equipment } from "domain/entities/equipment";
import { parseEquipmentAttributes } from "presentation/components/Equipment/equipment-utils";
import { useUpdateMyEquipmentAttributesMutation } from "presentation/hooks/use-query-equipments";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useState } from "react";
import MobileEquipmentForm from "../MobileEquipmentForm";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";

interface EquipmentDetailsProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    equipment: Equipment | null;
    setEquipment: (equipment: Equipment | null) => void;
    toastRef: RefObject<Toast | null>

} 

const EquipmentDetails = ({visible, setVisible, equipment, toastRef, setEquipment}: EquipmentDetailsProps) => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateAttributesMutation, isPending: isSaving } = useUpdateMyEquipmentAttributesMutation();

    const [attributesObject, setAttributesObject] = useState<Record<string, any>>({});

    // Igual à web (MyEquipmentsTab.handleOpenAttributes): carregar os atributos da viatura sempre que o detalhe abre
    useEffect(() => {
        if (visible && equipment?.id) {
            setAttributesObject(parseEquipmentAttributes(equipment.attributes));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, equipment?.id]);

    const handleClose = () => {
        setVisible(false);
        setEquipment(null);
        setAttributesObject({});
    };

    const handleSaveAttributes = async () => {
        if (!equipment?.id || isSaving) return;

        try {
            await updateAttributesMutation({
                id: equipment.id,
                attributes: JSON.stringify(attributesObject),
            });

            // Garante que a lista já tem os atributos atualizados antes de fechar,
            // para que ao reabrir os Detalhes apareça logo o valor guardado
            await queryClient.refetchQueries({ queryKey: ["equipments"] });

            toastRef.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Características guardadas.",
                life: 3000,
            });
            handleClose();
        } catch (err: unknown) {
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: getApiErrorMessage(err,"Erro ao guardar as características."),
                life: 3500,
            });
        }
    };

    const footer = (
        <div className="w-full flex justify-content-center align-items-center gap-3">
            <Button
                label="Cancelar"
                type="button"
                className="btn-mobile-secondary"
                style={{ width: "135px", height: "42px" }}
                disabled={isSaving}
                onClick={handleClose}
            />
            <Button
                label={isSaving ? "A guardar..." : "Guardar"}
                icon={isSaving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                type="button"
                className="btn-mobile"
                style={{ width: "135px", height: "42px" }}
                disabled={isSaving}
                onClick={handleSaveAttributes}
            />
        </div>
    );

    return (
        <MobileBottomSheet
            visible={visible}
            onHide={() => !isSaving && handleClose()}
            title="Características da Viatura"
            headerIcon="pi pi-info-circle"
            footer={footer}
            closable={!isSaving}
            dismissableMask={!isSaving}
        >
            <div className="py-2">
                {equipment?.id && (
                    <MobileEquipmentForm
                        selectedEquipment={equipment}
                        setSelectedEquipment={setEquipment}
                        attributesObject={attributesObject}
                        setAttributesObject={setAttributesObject}
                        isAdmin={false}
                        readOnlyBaseFields={true}
                        showOnlyVehicles={true}
                    />
                )}
            </div>
        </MobileBottomSheet>
    );
}

export default EquipmentDetails;
